import Header from '@/components/Header';
import { supabase } from '@/lib/supabase';
import { ArrowRight, History, Snowflake } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ChronologyPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [hasChristmasData, setHasChristmasData] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Load saved date from localStorage on initial render
  useEffect(() => {
    const savedDate = localStorage.getItem('selectedDate');
    if (savedDate) {
      setSelectedDate(savedDate);
    }
  }, []);

  // Check if Christmas data exists
  useEffect(() => {
    const checkChristmasData = async () => {
      setLoading(true);
      try {
        // Quick check: try to get at least one December 25th record
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_christmas_songs');

        if (!rpcError && rpcData && rpcData.length > 0) {
          setHasChristmasData(true);
        } else {
          // Fallback: check with a simple query
          const { data: sampleData } = await supabase
            .from('music_data')
            .select('broadcast_date')
            .like('broadcast_date', '%-12-25')
            .limit(1);

          setHasChristmasData(!!(sampleData && sampleData.length > 0));
        }
      } catch (error) {
        console.error('Failed to check Christmas data:', error);
        setHasChristmasData(false);
      } finally {
        setLoading(false);
      }
    };

    checkChristmasData();
  }, []);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    // Save to localStorage for persistence across pages
    localStorage.setItem('selectedDate', date);
    // Don't automatically navigate to Daily Setlist page
  };

  const handleSearchResultClick = (title: string, artist: string) => {
    // Handle search result click if needed
  };

  return (
    <div className="min-h-screen">
      <Header onDateChange={handleDateChange} onSearchResultClick={handleSearchResultClick} />

      <main className="container mx-auto max-w-7xl px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2 text-white">
            <History className="h-7 w-7 text-primary" />
            Chronology
          </h1>
          <p className="text-gray-400">Browse music chronologically</p>
        </div>

        {/* Christmas Special Card */}
        <button
          type="button"
          onClick={() => navigate('/chronology/christmas')}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-8 shadow-lg mb-8 cursor-pointer hover:bg-gray-800/70 transition-all hover:scale-[1.02] text-left"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <Snowflake className="h-8 w-8 text-blue-400 animate-pulse" />
            <h2 className="text-3xl font-bold text-white">Christmas Special</h2>
            <Snowflake className="h-8 w-8 text-blue-400 animate-pulse" />
          </div>
          <p className="text-gray-300 mb-6 text-center text-lg">
            매년 12월 25일 크리스마스에 들었던 노래들을 확인해보세요
          </p>
          <div className="flex items-center justify-center gap-2 text-blue-400">
            <span className="text-sm font-medium">자세히 보기</span>
            <ArrowRight className="h-5 w-5" />
          </div>
        </button>

        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg">
          <p className="text-center text-lg text-gray-400 mb-4">
            더 많은 연대기 기능이 곧 추가될 예정입니다
          </p>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Return to Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChronologyPage;
